import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, isManager, isAccountant, canWarehouse, isDesigner } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { Layout } from "@/components/Layout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { UpdateAvailable } from "@/components/shared/UpdateAvailable";
import Locked from "@/pages/Locked";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AcceptInvite from "@/pages/AcceptInvite";
import ResetPassword from "@/pages/ResetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Attendance from "@/pages/Attendance";
import LiveBoard from "@/pages/LiveBoard";
import Team from "@/pages/Team";
import TeamMemberDetail from "@/pages/TeamMemberDetail";
import Investors from "@/pages/Investors";
import InvestorDetail from "@/pages/InvestorDetail";
import Settings from "@/pages/Settings";
import AttendanceSheet from "@/pages/AttendanceSheet";
import Tasks from "@/pages/Tasks";
import TaskDetail from "@/pages/TaskDetail";
import Economics from "@/pages/Economics";
import AttendanceOverview from "@/pages/AttendanceOverview";
import DiaryOverview from "@/pages/DiaryOverview";
import Gallery from "@/pages/Gallery";
import PublicShare from "@/pages/PublicShare";
import Calculator from "@/pages/Calculator";
import DemoAction from "@/pages/DemoAction";
import Warehouse from "@/pages/Warehouse";
import Orders from "@/pages/Orders";
import Suppliers from "@/pages/Suppliers";
import SupplierDetail from "@/pages/SupplierDetail";
import Planning from "@/pages/Planning";
import Superadmin from "@/pages/Superadmin";
import MyInvoices from "@/pages/MyInvoices";

function Loader() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
      <img src="/logo-192.png" alt="Proyekty" className="w-16 h-16 animate-pulse" />
      <div className="text-2xl font-bold tracking-tight animate-pulse">Proyekty</div>
    </div>
  );
}

// Účetní má přístup jen do Ekonomiky a ke svému nastavení. Filtr je schválně
// tady, ne u jednotlivých rout — většina rout je totiž jen `Protected`
// a pokryje to i routy, které teprve vzniknou.
const ACCOUNTANT_PATHS = ["/ekonomika", "/nastaveni"];

function Protected({ children }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading || user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (isAccountant(user) && !ACCOUNTANT_PATHS.some((p) => pathname.startsWith(p)))
    return <Navigate to="/ekonomika/doklady" replace />;
  return <Layout>{children}</Layout>;
}

function HomeRedirect() {
  return <Dashboard />;
}

function ManagerOnly({ children }) {
  const { user } = useAuth();
  return isManager(user) ? children : <Navigate to="/" replace />;
}

// Deník a Gantt: vedení + projektant (173; Gantt jen ke čtení, rozsah hlídá server).
function ManagerOrDesigner({ children }) {
  const { user } = useAuth();
  return isManager(user) || isDesigner(user) ? children : <Navigate to="/" replace />;
}

// Galerie: vedení + stavbyvedoucí + dělník (rozsah fotek řídí server dle role).
function GalleryOnly({ children }) {
  const { user } = useAuth();
  return user && ["OWNER", "MANAGER", "ASSISTANT", "WORKER"].includes(user.role) ? children : <Navigate to="/" replace />;
}

// Ekonomika: vedení + účetní.
function EconomyOnly({ children }) {
  const { user } = useAuth();
  return isManager(user) || isAccountant(user) ? children : <Navigate to="/" replace />;
}

// Sklad: vedení + asistent/ka + skladník + stavbyvedoucí (náhled).
function WarehouseOnly({ children }) {
  const { user } = useAuth();
  return canWarehouse(user) ? children : <Navigate to="/" replace />;
}

// Objednávky (311): kancelář je zakládá, stavba a skladník přebírají. Projektant ne,
// účetní má jen Ekonomiku (ACCOUNTANT_PATHS výše).
function OrdersOnly({ children }) {
  const { user } = useAuth();
  return user && ["OWNER", "ASSISTANT", "MANAGER", "WORKER", "SUBCONTRACTOR", "WAREHOUSE"].includes(user.role) ? children : <Navigate to="/" replace />;
}

function SubOnly({ children }) {
  const { user } = useAuth();
  return user?.role === "SUBCONTRACTOR" ? children : <Navigate to="/" replace />;
}

// Tabule: jen majitel a stavbyvedoucí (asistent/ka ne).
function PlatformOnly({ children }) {
  const { user } = useAuth();
  return user?.is_platform_admin ? children : <Navigate to="/" replace />;
}

// Redirects for legacy finance URLs; maps ?upload=1 to the new type-specific param.
function LegacyRedirect({ to, uploadType }) {
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  if (p.get("upload") === "1" && uploadType) p.set("upload", uploadType);
  const qs = p.toString();
  return <Navigate to={`${to}${qs ? `?${qs}` : ""}`} replace />;
}

// Zamčená firma (blocked) vidí místo celé aplikace zamykací obrazovku.
// Grace zůstává beze změny (čtení jede, zápisy vrací 402). Superadmin dostává
// z /auth/me vždy access.state=active, takže se nikdy nezamkne.
function LockGate({ children }) {
  const { user, access } = useAuth();
  const { pathname } = useLocation();
  if (["/s/", "/payment", "/kalkulacka", "/demo/"].some((x) => pathname.startsWith(x))) return children; // veřejné stránky jedou vždy
  if (user && access?.state === "blocked") return <Locked reason={access.reason} />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/s/:token" element={<PublicShare />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/kalkulacka" element={<Calculator />} />
      <Route path="/demo/prodlouzit" element={<DemoAction action="prodlouzit" />} />
      <Route path="/demo/odhlasit" element={<DemoAction action="odhlasit" />} />

      <Route path="/" element={<Protected><HomeRedirect /></Protected>} />
      <Route path="/zakazky" element={<Protected><Projects /></Protected>} />
      <Route path="/zakazky/:id" element={<Protected><ProjectDetail /></Protected>} />
      <Route path="/ukoly" element={<Protected><Tasks /></Protected>} />
      <Route path="/ukoly/:id" element={<Protected><TaskDetail /></Protected>} />
      <Route path="/planovani" element={<Protected><ManagerOrDesigner><Planning /></ManagerOrDesigner></Protected>} />
      <Route path="/ekonomika" element={<Protected><EconomyOnly><Economics tab="overview" /></EconomyOnly></Protected>} />
      <Route path="/ekonomika/doklady" element={<Protected><EconomyOnly><Economics tab="documents" /></EconomyOnly></Protected>} />
      <Route path="/ekonomika/platby" element={<Protected><EconomyOnly><Economics tab="payments" /></EconomyOnly></Protected>} />
      <Route path="/ekonomika/pokladna" element={<Protected><EconomyOnly><Economics tab="cashbox" /></EconomyOnly></Protected>} />
      <Route path="/ekonomika/naskladneni" element={<Protected><EconomyOnly><Economics tab="stock" /></EconomyOnly></Protected>} />
      <Route path="/ekonomika/rezie" element={<Protected><EconomyOnly><Economics tab="overhead" /></EconomyOnly></Protected>} />
      <Route path="/doklady" element={<LegacyRedirect to="/ekonomika/doklady" uploadType="faktura" />} />
      <Route path="/uctenky" element={<LegacyRedirect to="/ekonomika/doklady" uploadType="uctenka" />} />
      <Route path="/vydaje" element={<LegacyRedirect to="/ekonomika/rezie" />} />
      <Route path="/dochazka" element={<Protected><Attendance /></Protected>} />
      <Route path="/dochazka/prehled" element={<Protected><ManagerOnly><AttendanceOverview /></ManagerOnly></Protected>} />
      <Route path="/denik" element={<Protected><ManagerOrDesigner><DiaryOverview /></ManagerOrDesigner></Protected>} />
      <Route path="/galerie" element={<Protected><GalleryOnly><Gallery /></GalleryOnly></Protected>} />
      <Route path="/sklad" element={<Protected><WarehouseOnly><Warehouse /></WarehouseOnly></Protected>} />
      <Route path="/objednavky" element={<Protected><OrdersOnly><Orders /></OrdersOnly></Protected>} />
      <Route path="/tabule" element={<Protected><ManagerOnly><LiveBoard /></ManagerOnly></Protected>} />
      <Route path="/tym" element={<Protected><ManagerOnly><Team /></ManagerOnly></Protected>} />
      <Route path="/moje-faktury" element={<Protected><SubOnly><MyInvoices /></SubOnly></Protected>} />
      <Route path="/tym/:id" element={<Protected><ManagerOnly><TeamMemberDetail /></ManagerOnly></Protected>} />
      <Route path="/investori" element={<Protected><ManagerOnly><Investors /></ManagerOnly></Protected>} />
      <Route path="/investori/:iid" element={<Protected><ManagerOnly><InvestorDetail /></ManagerOnly></Protected>} />
      <Route path="/dodavatele" element={<Protected><ManagerOnly><Suppliers /></ManagerOnly></Protected>} />
      <Route path="/dodavatele/:sid" element={<Protected><ManagerOnly><SupplierDetail /></ManagerOnly></Protected>} />
      <Route path="/nastaveni" element={<Protected><Settings /></Protected>} />
      <Route path="/dochazka/vykaz/:userId" element={<Protected><ManagerOnly><AttendanceSheet /></ManagerOnly></Protected>} />
      <Route path="/superadmin" element={<Protected><PlatformOnly><Superadmin /></PlatformOnly></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  // Telefon (197): oznámení pod plovoucí horní lištou a pod výřezem iPhonu, ne pod hodinami.
  return <Toaster position="top-center" richColors theme={theme === "dark" ? "dark" : "light"}
    mobileOffset={{ top: "max(66px, calc(env(safe-area-inset-top) + 58px))", left: "12px", right: "12px" }} />;
}

// Karta nové verze jen pro přihlášené — login zůstává čistý (155).
function UpdateGate() {
  const { user } = useAuth();
  return user ? <UpdateAvailable /> : null;
}

function App() {
  return (
    <div className="App">
      {/* Boundary je NAD providery — musí zachytit i pád ThemeProvideru
          nebo AuthProvideru, jinak zůstane prázdný #root (černá obrazovka). */}
      <ErrorBoundary>
        <ThemeProvider>
          <AuthProvider>
            <BrowserRouter>
              <LockGate>
                <AppRoutes />
              </LockGate>
              <ThemedToaster />
              <UpdateGate />
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
